import { Link } from "react-router-dom";
import { Phone, MessageCircle, Camera } from "lucide-react";
import { SITE, waLink } from "@/config/site";
import { usePageTitle, Reveal, Eyebrow } from "@/components/bits";
import QuoteForm from "@/components/QuoteForm";

const POINTS = [
  "Free to request — tell us what you need",
  "Send up to 5 photos of the area",
  "Serving every parish in Bermuda",
];

export default function QuotePage() {
  usePageTitle("Request a Quote");
  return (
    <>
      <section className="border-b border-white/10 bg-joell-coal/40" data-testid="quote-hero">
        <div className="container-x pb-12 pt-36 md:pb-14 md:pt-44">
          <Reveal>
            <Eyebrow>Free quotes</Eyebrow>
            <h1 className="mt-4 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-6xl">
              Request a free quote
            </h1>
            <p className="mt-4 max-w-2xl text-base text-slate-300 sm:text-lg">
              Tell JOELL what you need. We'll review your request and get back to you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-x py-14 md:py-20" data-testid="quote-page-form-section">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.6fr] lg:gap-16">
          <div>
            <Reveal>
              <h2 className="font-display text-2xl font-bold uppercase text-white">What happens next?</h2>
              <ul className="mt-6 space-y-4">
                {POINTS.map((p, i) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-slate-300" data-testid={`quote-page-point-${i}`}>
                    <span aria-hidden="true" className="mt-1 inline-block h-2 w-2 shrink-0 rotate-45 border-t-2 border-r-2 border-joell-green" />
                    {p}
                  </li>
                ))}
              </ul>
              <div className="mt-8 border border-white/10 bg-joell-surface p-6" data-testid="quote-page-faster-quote">
                <p className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-joell-yellow">
                  <Camera size={14} /> Get a faster quote
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                  Take a few photos of the area that needs work and send them straight to JOELL on WhatsApp.
                </p>
                <a
                  href={waLink("Hi JOELL, I'd like a faster quote. I'm sending photos of the area that needs work.")}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline mt-5 w-full !border-joell-yellow/50 !text-joell-yellow hover:!bg-joell-yellow/10"
                  data-testid="quote-page-whatsapp-button"
                >
                  <MessageCircle size={15} /> Send Photos on WhatsApp
                </a>
              </div>
              <div className="mt-6 border border-white/10 bg-joell-surface p-6 text-sm text-slate-400" data-testid="quote-page-call-box">
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Rather talk it through?</p>
                <a href={SITE.phoneHref} className="mt-3 flex items-center gap-2 font-display text-2xl font-bold text-white transition-colors hover:text-joell-yellow" data-testid="quote-page-call-button">
                  <Phone size={18} className="text-joell-yellow" /> {SITE.phone}
                </a>
                <a href={SITE.phone2Href} className="mt-1 flex items-center gap-2 font-display text-2xl font-bold text-white transition-colors hover:text-joell-yellow" data-testid="quote-page-call2-button">
                  <Phone size={18} className="text-joell-yellow" /> {SITE.phone2}
                </a>
              </div>
              <p className="mt-6 text-xs text-slate-500">
                Looking for something else? <Link to="/contact" className="text-joell-yellow underline underline-offset-4">Contact us</Link> or read{" "}
                <Link to="/about" className="text-joell-yellow underline underline-offset-4">about JOELL</Link>.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.08}>
            <QuoteForm />
          </Reveal>
        </div>
      </section>
    </>
  );
}
